import '../../modal/components/modal/modal.component.scss';
import './membership-modal-extensions.scss';

import type { FunctionComponent } from 'preact';
import { useCallback, useMemo, useState } from 'preact/hooks';
import { useDispatch } from 'react-redux';

import { useShopifyCurrency } from '../../common/currency/shopify-currency.hook';
import { useTranslation } from '../../i18n';
import { modalService } from '../../modal/modal.service';
import type { AppDispatch } from '../../store';
import type {
  ChangeType,
  Membership,
  MembershipContractUpdateRequest,
  Tier,
} from '../models/membership.model';
import { IntervalType } from '../models/membership.model';
import { updateMembership } from '../store/membership.thunk';
import {
  formatDate,
  formatTermBilling,
  getMembershipTermName,
} from '../utils/membership.formatters';
import type { MembershipConfirmCancellationModalProps } from './membership-confirm-cancellation.modal';
import { MembershipConfirmCancellationModal } from './membership-confirm-cancellation.modal';

type Phase = 'MANAGE' | 'CONFIRM';

export interface MembershipManageModalProps {
  membership: Membership;
  tier: Tier;
  showCancelBtn?: boolean;
}

export const MembershipManageModal: FunctionComponent<MembershipManageModalProps> = ({
  membership,
  tier,
  showCancelBtn = true,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { shopifyCurrency } = useShopifyCurrency();
  const { t } = useTranslation();

  const [phase, setPhase] = useState<Phase>('MANAGE');
  const [termId, setTermId] = useState<string>(membership?.selectedTerm?.id || '');
  const [legalTermsAccepted, setLegalTermsAccepted] = useState(false);

  const currentTerm = useMemo(() => membership.selectedTerm, [membership.selectedTerm]);
  const newTerm = useMemo(
    () => tier.terms?.find((term) => term.id === termId),
    [tier.terms, termId]
  );
  const legalTermsExist = useMemo(
    () => !!tier.legalTerms && tier.legalTerms.trim() !== '',
    [tier.legalTerms]
  );
  const isCancelling = useMemo(
    () => membership.latestPendingChange === 'CANCEL',
    [membership.latestPendingChange]
  );

  // Calculate cost comparison for terms (monthly equivalent)
  const termsWithCostComparison = useMemo(() => {
    if (!tier.terms) return [];
    return tier.terms.map((term) => {
      if (!term.costComparison) {
        const annualCost =
          (term.convertedPrice / term.intervalCount) *
          (term.intervalType === IntervalType.MONTH ? 12 : 52);
        return {
          ...term,
          costComparison: {
            discountAmount: 0,
            discountPercent: 0,
            annualCost,
          },
        };
      }
      return term;
    });
  }, [tier.terms]);

  const handleChangeTerm = useCallback(() => {
    const request: MembershipContractUpdateRequest = {
      contractId: membership.contractId,
      fanSubscriptionId: membership.id,
      tierId: tier.id,
      termId,
      changeType: 'TERM_CHANGE' as ChangeType,
      immediate: membership.selectedTier?.freeTier || false,
    };
    dispatch(updateMembership(request));
    modalService.closeAll();
  }, [
    membership.contractId,
    membership.id,
    membership.selectedTier?.freeTier,
    tier.id,
    termId,
    dispatch,
  ]);

  const handleSelectTerm = useCallback(() => {
    setLegalTermsAccepted(false);
    setPhase('CONFIRM');
  }, []);

  const handleGoBack = useCallback(() => {
    if (phase === 'CONFIRM') {
      setPhase('MANAGE');
    } else {
      modalService.closeAll();
    }
  }, [phase]);

  const handleCancelMembership = useCallback(() => {
    // Close the manage modal first to avoid having both modals open simultaneously
    modalService.closeAll();
    const membershipLabel =
      membership?.accountSettings?.membershipLabel === 'SUBSCRIPTION'
        ? 'Subscription'
        : 'Membership';
    const confirmCancellationModalProps: MembershipConfirmCancellationModalProps = {
      membership: membership,
      title: `Cancel ${membershipLabel}`,
      onClose: (confirmed: boolean) => {
        if (confirmed) {
          const request: MembershipContractUpdateRequest = {
            contractId: membership.contractId,
            fanSubscriptionId: membership.id,
            tierId: tier.id,
            termId: membership.selectedTerm.id,
            changeType: 'CANCEL' as ChangeType,
            immediate: membership.selectedTier?.freeTier || false,
          };
          dispatch(updateMembership(request));
        }
        modalService.closeAll();
      },
    };
    modalService.open({
      component: MembershipConfirmCancellationModal,
      props: confirmCancellationModalProps,
    });
  }, [
    membership,
    membership.accountSettings?.membershipLabel,
    membership.nextBillingDate,
    membership.contractId,
    membership.id,
    membership.selectedTerm.id,
    membership.selectedTier?.freeTier,
    tier.id,
    dispatch,
  ]);

  return (
    <div class="single-confirm-cancel-pending-action-modal">
      {phase === 'MANAGE' && (
        <>
          <div class="single-modal-header">
            <h3 class="single-modal-title">{t('membership.updateBillingTerms')}</h3>
          </div>
          <div class="single-modal-body">
            {termsWithCostComparison.map((term) => (
              <div
                key={term.id}
                className={`single-membership-term-option ${term.id === termId ? 'selected' : ''}`}
              >
                <label>
                  <input
                    type="radio"
                    name="term"
                    value={term.id}
                    checked={term.id === termId}
                    onChange={(e) => setTermId((e.target as HTMLInputElement).value)}
                  />
                  <div class="single-membership-label-text">
                    <div>
                      <b class="single-membership-term-length">{getMembershipTermName(term)}</b>
                      <span>
                        {' '}
                        ({shopifyCurrency((term.costComparison?.annualCost || 0) / 12, false)}
                        {t('membership.perMonth')})
                      </span>
                    </div>
                    <p class="single-membership-term-billing">{formatTermBilling(term)}</p>
                    {term.id === currentTerm?.id && (
                      <span class="single-membership-current-tag">
                        {t('membership.currentPlan')}
                      </span>
                    )}
                  </div>
                </label>
              </div>
            ))}
          </div>
          <div class="single-modal-footer modalSeparator single-action-list inline--btns">
            {!showCancelBtn && (
              <button class="btn btn-outline button" onClick={handleGoBack}>
                {t('membership.goBack')}
              </button>
            )}
            {showCancelBtn && !isCancelling && (
              <button class="btn button btn-red" onClick={handleCancelMembership}>
                {t('membership.cancelMembershipButton')}
              </button>
            )}
            {termsWithCostComparison.length > 1 && (
              <button
                class="btn button"
                onClick={handleSelectTerm}
                disabled={!termId || termId === currentTerm?.id}
              >
                {t('membership.continue')}
              </button>
            )}
          </div>
        </>
      )}
      {phase === 'CONFIRM' && (
        <>
          <div class="single-modal-header">
            <h3 class="single-modal-title">{t('membership.confirmYourChanges')}</h3>
          </div>
          <div class="single-modal-body">
            <div class="single-membership-term-info current">
              <div class="single-membership-term-detail">
                <b>{t('membership.currentPlan')}</b>
                <div>
                  <b class="single-membership-term-length">{getMembershipTermName(currentTerm)}</b>
                  <span class="single-membership-term-billing">
                    ({formatTermBilling(currentTerm)})
                  </span>
                </div>
              </div>
            </div>
            <div class="single-membership-term-info new">
              <div class="single-membership-term-detail">
                <b>{t('membership.newPlan')}</b>
                <div>
                  <b class="single-membership-term-length">{getMembershipTermName(newTerm)}</b>
                  <span class="single-membership-term-billing">
                    ({formatTermBilling(newTerm!)})
                  </span>
                </div>
              </div>
              <div class="single-membership-term-start">
                <span>{t('membership.newPlanStarts')}</span>
                <p>
                  <b>{formatDate(membership.nextBillingDate)}</b>
                </p>
              </div>
            </div>
            {legalTermsExist && (
              <div class="single-membership-legal-terms single-membership-separator">
                <div className="single-membership-input-group checkbox">
                  <input
                    type="checkbox"
                    id="legalTerms"
                    checked={legalTermsAccepted}
                    onChange={(e) => setLegalTermsAccepted((e.target as HTMLInputElement).checked)}
                  />
                  <label
                    htmlFor="legalTerms"
                    dangerouslySetInnerHTML={{ __html: tier.legalTerms }}
                  />
                </div>
              </div>
            )}
          </div>
          <div
            class={`single-modal-footer inline--btns ${!legalTermsExist ? 'single-membership-separator' : ''}`}
          >
            <button class="btn btn-outline button" onClick={handleGoBack}>
              {t('membership.goBack')}
            </button>
            <button
              class="btn button btn-gray"
              disabled={legalTermsExist && !legalTermsAccepted}
              onClick={handleChangeTerm}
            >
              {t('membership.confirmChange')}
            </button>
          </div>
        </>
      )}
    </div>
  );
};
